/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useMemo } from 'react';
import { IExpandedSlot } from '../types';
import { BnIcon } from '../../../components/icons_v2';
import { Typography } from '../../../atoms';
import { ESports, ETypography, ETypographyColor, IColors, Icons, sportsIcons } from '../../../types';
import { useColors } from '../../../hooks/useColors';
import { ResourceTypeEnum } from '../../../types/enums/resource';
import { en } from '../../../lib/i18n/en';

const secondaryBodyCss = (colors: IColors) => css`
	padding: 0.3rem 2rem;
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	max-width: 100%;
	gap: 0.3rem 2.6rem;
	border-top: 0.1rem solid ${colors.bg_border_seperator};
`;

const secondaryBodyLineCss = (colors: IColors, maxWidth: string) => css`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	width: min-content;
	max-width: ${maxWidth};
	margin: 1rem 0rem;
	gap: 0.3rem;
	svg {
		color: ${colors.tx_text_primary};
		width: 1.1rem;
		height: 1.1rem;
		flex: 0 0 auto;
	}
	div {
		display: block;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;

const TruncatedBodyLine = ({
	text,
	icon,
	width,
	colors
}: {
	text: string;
	icon: Icons;
	width: string;
	colors: IColors;
}) => {
	const secondaryBodyLineStyle = useMemo(() => secondaryBodyLineCss(colors, width), [colors]);
	return (
		<div css={secondaryBodyLineStyle}>
			<BnIcon icon={icon} />
			<Typography color={ETypographyColor.primary} type={ETypography.body2Accented}>
				{text}
			</Typography>
		</div>
	);
}

const Activity = ({
	expandedSlot,
	colors
}: {
	expandedSlot: IExpandedSlot;
	colors: IColors;
}) => {
	return expandedSlot?.sportIds?.[0] !== undefined ? (
		<TruncatedBodyLine
			text={en.ESports[expandedSlot.sportIds[0]] ?? en.ESports[ESports.OTHER]}
			icon={sportsIcons[expandedSlot.sportIds[0]] ?? sportsIcons[ESports.OTHER]}
			width={'12rem'}
			colors={colors}
			data-aid="popupBody-activity"
		/>
	) : null;
};

const Addons = ({
	expandedSlot,
	colors
}: {
	expandedSlot: IExpandedSlot;
	colors: IColors;
}) => {
	return expandedSlot?.addons && expandedSlot?.addons?.length > 0 ? (
		<TruncatedBodyLine
			text={expandedSlot.addons.map(addon => addon?.productUser?.productName ?? null).join(', ')}
			icon={Icons.add_ons}
			width={'28rem'}
			colors={colors}
			data-aid="popupBody-addons"
		/>
	) : null;
};

const Instructors = ({
	expandedSlot,
	colors
}: {
	expandedSlot: IExpandedSlot;
	colors: IColors;
}) => {
	return expandedSlot?.resources && expandedSlot?.resources?.length > 0 ? (
		<TruncatedBodyLine
			text={expandedSlot.resources
				.filter(resource => resource.resourceType === ResourceTypeEnum.INSTRUCTOR)
				.map(instructor => `${instructor.firstName} ${instructor.lastName}`)
				.join(', ')}
			icon={Icons.instructor}
			width={'28rem'}
			colors={colors}
			data-aid="popupBody-instructors"
		/>
	) : null;
};

export interface ISecondaryBodyLabels {
	sportLabel: string
}

export const SecondaryBody = ({
	expandedSlot,
}: {
	expandedSlot: IExpandedSlot;
}) => {
	const { colors } = useColors();
	const secondaryBodyStyle = useMemo(() => secondaryBodyCss(colors), [colors]);
	return (
		<div data-aid="popupBody-secondaryBody" css={secondaryBodyStyle}>
			<Activity expandedSlot={expandedSlot} colors={colors} />
			<Instructors expandedSlot={expandedSlot} colors={colors} />
			<Addons expandedSlot={expandedSlot} colors={colors} />
		</div>
	);
}
